import React from 'react';
import { Download, ArrowRight, Shield, Zap, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EcoFinex = () => {
  const handleDownloadBrochure = () => {
    const downloadUrl = `${API}/download-brochure`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `EcoFinex_Product_Catalog_${new Date().getFullYear()}.pdf`);
    link.setAttribute('target', '_self');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  };

  return (
    <div className="ecofinex-page bg-white min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center">
              <img 
                src="/ecofinex-logo.png" 
                alt="EcoFinex Logo" 
                className="h-10"
              />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm text-gray-700 hover:text-blue-600 transition-colors font-medium">Home</a>
              <a href="/shades" className="text-sm text-gray-700 hover:text-blue-600 transition-colors font-medium">Shades</a>
              <a href="#contact" className="text-sm text-gray-700 hover:text-blue-600 transition-colors font-medium">Contact Us</a>
              <button
                onClick={handleDownloadBrochure}
                data-testid="download-brochure-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <Download className="w-4 h-4" />
                Download Brochure
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo-style ECOFINEX text */}
            <h1 
              className="text-6xl lg:text-7xl font-black tracking-tight mb-6"
              style={{ 
                fontFamily: "'Montserrat', sans-serif",
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 30%, #4a90a4 50%, #6db3c4 70%, #1e3a5f 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                letterSpacing: '0.02em'
              }}
            >
              ECOFINEX
            </h1>
            <p className="text-3xl lg:text-4xl font-light text-blue-600 mb-4">
              Powder. Precision. Performance.
            </p>
            <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Premium powder coating resins engineered for perfection. From vibrant metallics to sophisticated mattes, discover industrial-grade finishes that transform surfaces.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/shades"
                data-testid="explore-shades-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Explore Shades <ArrowRight className="w-4 h-4" />
              </a>
              <button
                onClick={handleDownloadBrochure}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-full text-sm font-medium hover:border-blue-600 hover:text-blue-600 transition-all duration-300 hover:scale-105"
              >
                <Download className="w-4 h-4" />
                Get Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gray-50 rounded-3xl border border-gray-100">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Industrial Grade</h3>
              <p className="text-sm text-gray-600">Engineered for durability and performance in demanding environments</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-3xl border border-gray-100">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Curing</h3>
              <p className="text-sm text-gray-600">Optimized formulations for quick production cycles</p>
            </div>
            <div className="text-center p-8 bg-gray-50 rounded-3xl border border-gray-100">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Premium Quality</h3>
              <p className="text-sm text-gray-600">Superior finish with exceptional color consistency</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shades Preview Section */}
      <section id="shades" className="py-20 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Shade Collection</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Discover over 200 premium powder coating shades across 7 finish categories. From vibrant metallics 
              to sophisticated mattes, find the perfect finish for your project.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">200+ Shades</h3>
              <p className="text-gray-600">Extensive color palette across multiple finish types</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">7 Finish Types</h3>
              <p className="text-gray-600">Glossy, Matt, Satin, Structure, Texture, Metallic, Special</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Competitive Pricing</h3>
              <p className="text-gray-600">Competitive pricing with 10kg minimum order</p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a
              href="/shades"
              className="inline-flex items-center justify-center gap-3 px-12 py-5 bg-blue-600 text-white rounded-full text-lg font-medium hover:bg-blue-700 hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              Explore All Shades
            </a>
            <p className="text-sm text-gray-500 mt-4">
              Browse our complete collection of powder coating shades
            </p>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-lg text-gray-600">Get in touch with our team for quotes, inquiries, or support</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-gray-50 border border-gray-200 p-8 rounded-3xl">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Get In Touch</h3>
                
                {/* Address */}
                <div className="mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Our Location</h4>
                      <p className="text-gray-600 leading-relaxed">
                        Plot No. 40, Women's Park,<br />
                        1st Stage, Gamangatti Industrial Area,<br />
                        Hubballi, Karnataka
                      </p>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Call Us</h4>
                      <p className="text-gray-600">
                        <a href="tel:+919886236644" className="hover:text-blue-600 transition-colors">+91-9886236644</a><br />
                        <a href="tel:+919820553098" className="hover:text-blue-600 transition-colors">+91-9820553098</a><br />
                        <a href="tel:+919113262817" className="hover:text-blue-600 transition-colors">+91-9113262817</a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Email Us</h4>
                      <p className="text-gray-600">
                        <a href="mailto:ecofinex01@gmail.com" className="hover:text-blue-600 transition-colors">
                          ecofinex01@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Hours</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span className="font-medium">Monday - Saturday:</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Sunday:</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="space-y-6">
              <div className="bg-gray-100 rounded-3xl overflow-hidden h-[400px] flex items-center justify-center border border-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3846.962823873447!2d75.0491925751215!3d15.378499585205365!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTXCsDIyJzQyLjYiTiA3NcKwMDMnMDYuNCJF!5e0!3m2!1sen!2sin!4v1771319512726!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="EcoFinex Location"
                ></iframe>
              </div>

              {/* Quick Contact CTA */}
              <div className="bg-blue-600 p-8 rounded-3xl text-white">
                <h3 className="text-2xl font-semibold mb-4">Need a Quick Quote?</h3>
                <p className="mb-6 text-blue-100">
                  Browse our shade collection and request a quote directly from any product page. 
                  We respond within 24 hours!
                </p>
                <a
                  href="/shades"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-full font-medium hover:bg-gray-100 transition-all duration-300 hover:scale-105"
                >
                  Browse Shades
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-8">
            {/* Company Info */}
            <div>
              <h3 
                className="text-xl font-black mb-4 uppercase"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                EcoFinex
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                Powder. Precision. Performance. Premium powder coating resins engineered for perfection.
              </p>
              <p className="text-xs text-gray-500">
                Plot No. 40, Women's Park,<br />
                Gamangatti Industrial Area,<br />
                Hubballi, Karnataka
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-sm text-gray-400 hover:text-white transition-colors">Home</a></li>
                <li><a href="/shades" className="text-sm text-gray-400 hover:text-white transition-colors">Shade Collection</a></li>
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-white transition-colors">Contact Us</a></li>
                <li>
                  <button
                    onClick={handleDownloadBrochure}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Download Brochure
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+919886236644" className="hover:text-white">+91-9886236644</a>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+919113262817" className="hover:text-white">+91-9113262817</a>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:ecofinex01@gmail.com" className="hover:text-white">ecofinex01@gmail.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <span>www.ecofinexhubli.com</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-500 mb-2">
              Powder. Precision. Performance.
            </p>
            <p className="text-xs text-gray-600">
              © {new Date().getFullYear()} EcoFinex. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EcoFinex;
